import lodashAttempt from 'lodash/attempt';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';
import lodashIsError from 'lodash/isError';

import constants from '../Config/constants';
import { getCurrency } from '../Helper/Routines';
import routeList, { routeGroup } from '../Routes/list';
import { filter as filterDV } from '../RTK/defaultValues';

const { useCase, homeInitialFilterValue, searchInitialFilterValue } = filterDV;

const EMPTY_OBJ = {};

function anonymize(text, afterCharCount = 3, lastCharCount) {
  if (!text) {
    return text;
  }
  const characterToHide = text.substring(
    afterCharCount,
    text.length - (lastCharCount || 0),
  );
  const characterToReplace = '*'.repeat(characterToHide.length);
  let result = text.substr(0, afterCharCount) + characterToReplace;
  if (lastCharCount && lastCharCount > 0) {
    result += text.substr(text.length - lastCharCount, lastCharCount);
  }
  return result;
}

const capitalize = (str) => {
  if (typeof str === 'object') return str[0];
  if (!str) return str;
  const [first, ...rest] = str;
  return `${first.toUpperCase()}${rest.join('')}`;
};

/**
 * Used for formating the card number by adding space between certain gaps
 * @param {*} value @string or @number - card number
 * @param {*} gaps @array of @number - array of number where is the string index position to add space
 * @returns {string} // formatted card number
 */
const formatCardNumber = (value, gaps) => {
  if (lodashIsEmpty(gaps)) {
    return value;
  }
  value = value.replace(/\s/g, ''); // remove all spacing
  let formattedValue = gaps.reduce((acc, pos, i) => {
    // If the gap exceeds the current string length, stop slicing and return the accumulated string
    if (pos > value.length) {
      return acc + value.slice(gaps[i - 1] || 0);
    }
    const start = gaps[i - 1] || 0;
    return acc + value.slice(start, pos) + ' ';
  }, '');
  formattedValue += value.slice(gaps[gaps.length - 1]); // Add any remaining part of the string after the last gap
  return formattedValue.trim(); // remove trailing spaces
};

const formatDistance = (distance) => {
  if (!distance) {
    return `< 0.1 ${constants.DISTANCE_UNIT}`;
  } else {
    return `${distance} ${constants.DISTANCE_UNIT}`;
  }
};
const formatDistanceFromMeters = (distance) => {
  const { DISTANCE_UNIT } = constants; // e.g., "km"

  if (!distance || distance < 100) {
    return `< 0.1 ${DISTANCE_UNIT}`;
  }
  const km = distance / 1000;
  return `${km.toFixed(1)} ${DISTANCE_UNIT}`;
};

/**
 * Used for formatting the duration of store delivery time, code reference: https://stackoverflow.com/a/8495907
 * @param {*} duration @number
 * @returns {number}
 */
const formatDuration = (duration) => Math.round(duration / 5) * 5;

/**
 * Used for formatting the prep time for display purpose
 * @param {*} prepTime @number
 * @returns {string} e.g: "1 hr", "1 hr & 30min.", "25 min.", "8 hrs", etc.
 */
const formatPrepTime = (prepTime) => {
  if (prepTime < 60) return `${prepTime} min.`;
  const hours = Math.floor(prepTime / 60);
  const minutes = prepTime % 60;
  return `${hours} hr${hours > 1 ? 's' : ''}${
    minutes ? ` & ${minutes} min.` : ''
  }`;
};

/**
 * Get QR value for event ticket
 * @param {*} id - event id
 * @param {*} index - index from the quantity of booked event, start from 0
 * @returns {string}
 */
const getEventQr = (id, index) => `qrg://${id}_${index + 1}`;

const isDefaultFilter = (
  tagFilter,
  categoryFilter = [],
  useIn = useCase.HOME,
) => {
  // set filters
  const defaultFilter =
    useIn === useCase.HOME ? homeInitialFilterValue : searchInitialFilterValue;
  const currentFilter = { category: categoryFilter, tags: tagFilter };
  // set category filter
  const defaultCategory = defaultFilter.category;
  const currentCategory = currentFilter.category;
  // set tags filter
  const defaultTags = defaultFilter.tags.map((f) => f.paramName);
  const currentTags = currentFilter.tags.map((f) => f.paramName);
  // return if the current category and tags filter is match to the default filter
  return (
    lodashIsEqual(currentTags, defaultTags) &&
    lodashIsEqual(currentCategory, defaultCategory)
  );
};

/**
 * Check if user does not setup it's details yet in profile if no first, last or phone data
 * @param {*} user - object: user details
 * @param {optional} [isCamelCase] - boolean: if want to check as camelcase
 * @returns {boolean}
 */
const isUserDetailsIncomplete = (user, isCamelCase) => {
  if (isCamelCase) {
    return !user?.firstName || !user?.lastName || !user?.phone;
  } else {
    return !user?.first_name || !user?.last_name || !user?.phone;
  }
};

/**
 * Check if json is parsable or not
 * @param {*} jsonStr json stringify value
 * @returns
 */
const isValidJson = (jsonStr) => {
  return !lodashIsError(lodashAttempt(JSON.parse.bind(null, jsonStr)));
};

/**
 * Create array from number
 * @param {number} num - The numeric value to be converted to array.
 * @returns {Array} The converted number to array e.g: num=5 will return [0,1,2,3,4] // 5 elements array
 */
const numberToArray = (num) => {
  return Array.from(Array(num).keys());
};

/**
 * Converts a numeric value to its corresponding English word representation.
 * @param {number} num - The numeric value to be converted to words.
 * @returns {string} The English word representation of the input number.
 */
const numberToWord = (num) => {
  // base on this https://www.thoughtco.com/how-to-convert-numbers-to-words-with-javascript-4072535
  // so far we only need 0 - 5, if in case we needed something bigger number refer to the link above
  const numStrList = ['zero', 'one', 'two', 'three', 'four', 'five'];
  return numStrList[num];
};

const pluralize = (count, text) => {
  // this function only support adding s at the end of text
  // adjust this if need to have ed, ies etc.
  return `${text}${count > 1 ? 's' : ''}`;
};

const toMs = ({ hrs = 0, min = 0, sec = 0 }) =>
  (hrs * 60 * 60 + min * 60 + sec) * 1000;

/**
 * General get price range function for table and event
 * @param {number[]} prices - Array of  number prices
 * @returns {string} e.g: P1,000.00 - P10,000.00 or P200.00
 */
const getPriceRange = (prices) => {
  const sortedPrices = prices.sort((a, b) => (a < b ? -1 : 1)); // sort prices lowest to highest
  const firstAndLast = [
    getCurrency(sortedPrices[0]),
    getCurrency(sortedPrices[sortedPrices.length - 1]),
  ]; // get first and last
  const distinctPrices = [...new Set(firstAndLast)]; // remove same value
  return distinctPrices.join(' - '); // return as string
};

/**
 * Get price range of event tickets
 * @param {import('../RTK/api/events/types').EventTicket[]} data - Ticket Details
 * @returns {ReturnType<typeof getPriceRange>}
 */
const getEventPriceRange = (data) => {
  const allPrice = data.map((t) => t.item.price); // all price of ticket as single array
  return getPriceRange(allPrice);
};

/**
 * Get price range of table
 * @param {import('../RTK/api/reservations/types').TableExperience[]} data - Ticket Details
 * @returns {ReturnType<typeof getPriceRange>}
 */
const getTablePriceRange = (data) => {
  // all price of ticket as single array
  const allPrice = data.reduce(
    (a, b) => [...a, ...b.items.map((item) => Number(item.price))],
    [],
  );
  return getPriceRange(allPrice);
};

/**
 * @typedef {import('../Screens/ReservationCheckout/types').ReservationCheckout} CheckoutData
 * @typedef {typeof filterDV.reservationStoreFilterValue} Filter
 * @typedef {import('../Screens/Reservation/components/Card').Query} Params
 * @typedef {keyof constants.RESERVATION_TYPE} DefaultTab
 */
/**
 * Get the proper URL parameters based on the provided data source when navigating to the reservation store page
 * @param {{
 *   checkoutData?: CheckoutData,
 *   filter?: Filter,
 *   params?: Params & { storeId: string }
 * }} props
 * @param {DefaultTab} defaultTab - The default tab to be selected, either "dining" or "event"
 * @returns {{
 *   date: import('../types/date').DBDATE,
 *   defaultTab: DefaultTab,
 *   eventDateRange: number,
 *   partySize: number,
 *   storeId: string,
 *   time: import('../types/date').DBTIME
 * }} The constructed URL parameters
 */
const getReservationStoreParams = (
  { checkoutData, filter, params },
  defaultTab,
) => {
  const date = checkoutData?.selectedDate || filter?.tableDate || params?.date;
  const eventDateRange =
    filter?.eventDateRange ||
    params?.event_date_range ||
    constants.eventFilterData[1].value;
  const partySize =
    checkoutData?.size || filter?.partySize || params?.party_size;
  const storeId = checkoutData?.storeId || filter?.storeId || params?.storeId;
  const time = checkoutData?.selectedTime || filter?.tableTime || params?.time;
  return {
    date,
    defaultTab,
    eventDateRange,
    partySize,
    storeId,
    time,
  };
};

/**
 * @typedef {typeof routeGroup} RouteGroup
 * @typedef {keyof RouteGroup} RouteGroupName
 */
/**
 * Check if a route belongs to defined groups
 * @param {string} routeName
 * @returns {Record<RouteGroupName, boolean> & {name: string | undefined}}
 */
const isRouteInGroup = (routeName) => {
  if (!routeName) return EMPTY_OBJ;
  const rootTab = [routeList.HOME, routeList.RESERVATION]; // needed to add _tab at the end of the name
  const result = Object.fromEntries(
    Object.entries(routeGroup).map(([group, routes]) => [
      group,
      routes.includes(routeName),
    ]),
  );
  result.name = rootTab.includes(routeName) ? `${routeName}_tab` : routeName;
  return result;
};

/**
 * Joins an array of strings with a specified separator, using a different separator for the last element.
 * @example
 * // returns "1, 2, 3 and 4"
 * smartJoin([1, 2, 3, 4]);
 * @example
 * // returns "Tea and Coffee"
 * smartJoin(['Tea', 'Coffee']);
 * @example
 * // returns "Bread"
 * smartJoin(['Bread']);
 * @example
 * // returns ""
 * smartJoin([]);
 * @param {Array<string>} arr - The array of strings to join.
 * @param {string} [separator=', '] - The separator to use for all but the last element.
 * @param {string} [lastSeparator=' and '] - The separator to use for the last element.
 * @returns {string} The joined string.
 */
const smartJoin = (arr, separator = ', ', lastSeparator = ' and ') => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  if (arr.length === 1) return String(arr[0]);
  if (arr.length === 2) return arr.join(lastSeparator);
  return arr.slice(0, -1).join(separator) + lastSeparator + arr[arr.length - 1];
};

export {
  anonymize,
  capitalize,
  formatCardNumber,
  formatDistance,
  formatDistanceFromMeters,
  formatDuration,
  formatPrepTime,
  getEventQr,
  isDefaultFilter,
  isUserDetailsIncomplete,
  isValidJson,
  numberToArray,
  numberToWord,
  pluralize,
  toMs,
  getEventPriceRange,
  getTablePriceRange,
  getReservationStoreParams,
  isRouteInGroup,
  smartJoin,
};
